import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

export const getPaginationItems = (currentPage = 0, totalPages = 0) => {
  if (totalPages <= 7) {
    return [...Array(totalPages)].map((_, index) => index);
  }

  const items = new Set([0, 1, totalPages - 2, totalPages - 1]);

  [currentPage - 1, currentPage, currentPage + 1].forEach((page) => {
    if (page >= 0 && page < totalPages) {
      items.add(page);
    }
  });

  return [...items]
    .sort((a, b) => a - b)
    .reduce((acc, page, index, pages) => {
      if (index > 0 && page - pages[index - 1] > 1) {
        acc.push("ellipsis");
      }
      acc.push(page);
      return acc;
    }, []);
};

function PageControl({ page, currentPage, routePath, queryParam, onPage }) {
  const active = page === currentPage;
  const content = page + 1;
  const className = `flex h-10 min-w-10 items-center justify-center rounded-lg px-3 text-sm font-semibold transition-colors ${
    active
      ? "bg-primary text-white"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  }`;

  if (routePath) {
    return (
      <Link className={className} to={`${routePath}/${page + 1}${queryParam}`}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={className} onClick={() => onPage(page)}>
      {content}
    </button>
  );
}

function ArrowControl({
  disabled,
  direction,
  page,
  routePath,
  queryParam,
  onPage,
}) {
  const Icon = direction === "previous" ? ChevronLeftIcon : ChevronRightIcon;
  const className = `flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
    disabled
      ? "cursor-not-allowed text-slate-300"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
  }`;

  if (routePath && !disabled) {
    return (
      <Link className={className} to={`${routePath}/${page + 1}${queryParam}`}>
        <Icon className="h-5 w-5" />
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={() => onPage(page)}
      aria-label={direction === "previous" ? "Previous page" : "Next page"}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}

function TablePaginationFooter({
  currentPage = 0,
  totalPages = 0,
  pageSize,
  totalItems,
  currentItemCount,
  routePath,
  query = "",
  onPage,
}) {
  if (!totalPages || totalPages <= 1) {
    return null;
  }

  const queryParam = query ? `?${query}` : "";
  const firstItem =
    typeof totalItems === "number" && totalItems > 0
      ? currentPage * pageSize + 1
      : null;
  const lastItem =
    firstItem !== null
      ? Math.min(firstItem + (currentItemCount ?? pageSize) - 1, totalItems)
      : null;
  const previousPage = Math.max(currentPage - 1, 0);
  const nextPage = Math.min(currentPage + 1, totalPages - 1);

  return (
    <div className="flex flex-col gap-4 border-t border-slate-200 bg-slate-50 px-5 py-5 text-slate-600 md:flex-row md:items-center md:justify-between">
      <div className="text-sm">
        {firstItem !== null
          ? `Showing ${firstItem} to ${lastItem} of ${totalItems} entries`
          : null}
      </div>
      <nav className="flex items-center justify-end gap-1" aria-label="Pagination">
        <ArrowControl
          disabled={currentPage <= 0}
          direction="previous"
          page={previousPage}
          routePath={routePath}
          queryParam={queryParam}
          onPage={onPage}
        />
        {getPaginationItems(currentPage, totalPages).map((item, index) =>
          item === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="flex h-10 min-w-10 items-center justify-center px-2 text-sm font-semibold"
            >
              ...
            </span>
          ) : (
            <PageControl
              key={item}
              page={item}
              currentPage={currentPage}
              routePath={routePath}
              queryParam={queryParam}
              onPage={onPage}
            />
          )
        )}
        <ArrowControl
          disabled={currentPage >= totalPages - 1}
          direction="next"
          page={nextPage}
          routePath={routePath}
          queryParam={queryParam}
          onPage={onPage}
        />
      </nav>
    </div>
  );
}

export default TablePaginationFooter;
